import { useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";
import { SensitiveMoney } from "@/components/common/Sensitive";
import type { KaiActionProposal } from "./kaiTypes";

function usd(value: number | null | undefined): string {
  if (value == null) return "—";
  return `$${Math.round(value).toLocaleString()}`;
}

function isInternal(url: string | undefined): url is string {
  return typeof url === "string" && url.startsWith("/");
}

/**
 * Parse and validate an `action_proposal` fenced-block body. Returns `null` on
 * any shape mismatch, and rejects external URLs so a proposal can only
 * deep-link inside the app.
 */
export function parseActionProposalSpec(json: string): KaiActionProposal | null {
  try {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.title !== "string" || !parsed.title) return null;
    if (typeof parsed.kind !== "string" || !parsed.kind) return null;
    if (parsed.primary_action && !isInternal(parsed.primary_action.url)) {
      return null;
    }
    if (Array.isArray(parsed.secondary_actions)) {
      for (const a of parsed.secondary_actions) {
        if (!isInternal(a?.url)) return null;
      }
    }
    return parsed as KaiActionProposal;
  } catch {
    return null;
  }
}

function SummaryCell({
  label,
  amount,
  emphasis,
}: {
  label: string;
  amount: number | null | undefined;
  emphasis?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={
          emphasis
            ? "text-lg font-semibold text-emerald-600 dark:text-emerald-400"
            : "text-lg font-semibold text-foreground"
        }
      >
        <SensitiveMoney formatted={usd(amount)} />
      </span>
    </div>
  );
}

/**
 * Renders an `action_proposal` artifact: a headline, an optional before/after
 * money summary, and internal deep-link buttons. When `requires_elicitation`
 * is set the proposal is informational — the actual write still goes through
 * the normal confirmation path.
 */
export function KaiActionProposal({ spec }: { spec: KaiActionProposal }) {
  const navigate = useNavigate();
  const { summary } = spec;

  return (
    <div className="glass-panel my-2 flex flex-col gap-3 rounded-xl p-4">
      <p className="text-sm font-semibold text-foreground">{spec.title}</p>

      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <SummaryCell label="Current" amount={summary.current_all_in_usd} />
          <SummaryCell label="Proposed" amount={summary.proposed_all_in_usd} />
          <SummaryCell label="Savings" amount={summary.savings_usd} emphasis />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {spec.primary_action && (
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => navigate(spec.primary_action!.url)}
          >
            <ExternalLink className="size-3.5" />
            {spec.primary_action.label}
          </Button>
        )}
        {spec.secondary_actions?.map((action, i) => (
          <Button
            key={i}
            size="sm"
            variant="outline"
            onClick={() => navigate(action.url)}
          >
            {action.label}
          </Button>
        ))}
      </div>

      {spec.requires_elicitation && (
        <p className="text-xs text-muted-foreground">
          Confirming this change opens a confirmation step before anything is
          written.
        </p>
      )}
    </div>
  );
}
